import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { CheckIcon, XMarkIcon, GlobeAltIcon, CurrencyRupeeIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { useSubscriptionStore, asBillingCurrency } from "../stores/subscriptionStore";
import { ApolloClient, NormalizedCacheObject, useApolloClient, useQuery } from "@apollo/client";
import { useAuthStore } from "../stores/authStore";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { GET_MY_ORGANIZATION_SETUP_FEE_STATUS } from "../graphql/operations/subscription";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { Spinner } from "../components/ui/Spinner";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { PRICING_FAQS } from "../data/seo/faqs";
import ManagedSupportPromo from "../components/managedSupport/ManagedSupportPromo";
import { useShopifyBillingGate } from "../hooks/useShopifyBillingGate";
import UsagePricing from "../components/pricing/UsagePricing";
import { planFeatureLimitLabel } from "../components/pricing/formatPlanFeature";

const CURRENCY_META: Record<string, { symbol: string; label: string }> = {
    usd: { symbol: "$", label: "USD" },
    inr: { symbol: "₹", label: "INR" },
    eur: { symbol: "€", label: "EUR" },
};

const Pricing: React.FC = () => {
    const navigate = useNavigate();
    const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
    const { isAuthenticated, currentOrganization } = useAuthStore();
    const { pricingPlans, currentSubscription, isLoading, error, selectedCurrency, selectedBillingCycle, fetchPricingPlans, createCheckoutSession, setSelectedCurrency, setSelectedBillingCycle, clearError } = useSubscriptionStore();

    const { isShopifyManaged, stripeRailAllowed, shopifyBillingUrl } = useShopifyBillingGate();

    type SetupFeeService = "chat" | "call";
    const [isProcessing, setIsProcessing] = useState(false);
    const [setupFeeSelections, setSetupFeeSelections] = useState<Record<string, boolean>>({});
    const [setupServiceSelections, setSetupServiceSelections] = useState<Record<string, Set<SetupFeeService>>>({});
    const [setupFeeDescriptionExpanded, setSetupFeeDescriptionExpanded] = useState<Record<string, boolean>>({});
    const { data: setupFeeStatusData } = useQuery(GET_MY_ORGANIZATION_SETUP_FEE_STATUS, {
        skip: !isAuthenticated,
        variables: {
            organizationId: currentOrganization || undefined,
        },
        fetchPolicy: "cache-and-network",
    });

    // SEO + structured data — only meaningful on the public (unauthenticated)
    // hero design, but the hook calls are at the top of the component so
    // React doesn't see conditional hooks. The marketing meta is a no-op for
    // the dashboard view because logged-in users land on `/pricing` and
    // immediately render the dashboard variant.
    usePageSeo({
        title: "Pricing — AI Chat & Voice Agents Plans | FloGPT",
        description:
            "Transparent FloGPT plans for AI chat widgets, AI voice agents, knowledge base, workflows and outbound triggers. Free plan: $0, 100 messages/month. Paid plans from $49/month, or save up to 20% annually.",
        keywords: "FloGPT pricing, AI chatbot pricing, AI voice agent pricing, conversational AI plans, chatbot SaaS",
        canonical: "/pricing",
    });

    const productSchema = useMemo(() => {
        const offers = (pricingPlans || []).map((plan) => ({
            "@type": "Offer",
            name: plan.name,
            url: `${marketingOrigin()}/pricing`,
            priceCurrency: (selectedCurrency || "USD").toUpperCase(),
            price: String(
                selectedBillingCycle === "yearly" ? (plan as any).yearlyPrice ?? 0 : (plan as any).monthlyPrice ?? 0,
            ),
            availability: "https://schema.org/InStock",
            category: selectedBillingCycle === "yearly" ? "Annual subscription" : "Monthly subscription",
        }));
        return {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "FloGPT — AI Chat & Voice Agents",
            description:
                "AI Chat & Voice Agents platform. Embed AI chat on any website and run AI phone agents 24/7, grounded in your own knowledge base.",
            image: `${marketingOrigin()}/images/og-default.jpg`,
            brand: { "@type": "Brand", name: "FloGPT" },
            ...(offers.length
                ? {
                      offers: {
                          "@type": "AggregateOffer",
                          offerCount: offers.length,
                          priceCurrency: (selectedCurrency || "USD").toUpperCase(),
                          lowPrice: Math.min(...offers.map((o) => Number(o.price) || 0)).toString(),
                          highPrice: Math.max(...offers.map((o) => Number(o.price) || 0)).toString(),
                          offers,
                      },
                  }
                : {}),
        };
    }, [pricingPlans, selectedCurrency, selectedBillingCycle]);

    const availableCurrencies = useMemo(() => {
        return Array.from(
            new Set(
                pricingPlans
                    .flatMap((plan) => (Array.isArray(plan.prices) ? plan.prices.map((price) => String(price.currency || "").toLowerCase()) : []))
                    .filter(Boolean)
            )
        );
    }, [pricingPlans]);

    useEffect(() => {
        fetchPricingPlans(client);
    }, [client, fetchPricingPlans]);

    useEffect(() => {
        if (availableCurrencies.length > 0 && !availableCurrencies.includes(selectedCurrency)) {
            setSelectedCurrency(asBillingCurrency(availableCurrencies[0]));
        }
    }, [availableCurrencies, selectedCurrency, setSelectedCurrency]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            clearError();
        }
    }, [error, clearError]);

    const handleSelectPlan = async (planName: string) => {
        if (!isAuthenticated) {
            navigate("/signup");
            return;
        }

        // Defence-in-depth: even though the dashboard variant short-circuits
        // to the Shopify Billing panel below, a stale-state Shopify org
        // that somehow reaches this handler should never hit Stripe Checkout
        // — Shopify App Store rule 1.2.1 forbids any off-platform billing
        // step for these merchants. Bouncing them to the embedded app keeps
        // the flow within Shopify's standard billing modal.
        if (isShopifyManaged) {
            if (shopifyBillingUrl) {
                window.open(shopifyBillingUrl, "_blank", "noopener,noreferrer");
            }
            return;
        }
        if (!stripeRailAllowed) return;

        const plan = pricingPlans.find((p) => p.name === planName);
        const priceInfo = plan?.prices.find((p) => p.currency === selectedCurrency);

        if (!priceInfo) {
            toast.error("Could not find pricing information for the selected currency.");
            return;
        }

        if (!plan) {
            toast.error("Could not find the selected plan.");
            return;
        }

        const applySetupFee = shouldApplySetupFeeForPlan(plan);
        const setupFeeServices: SetupFeeService[] = applySetupFee ? getPlanSetupFeeServices(plan) : [];

        setIsProcessing(true);
        try {
            const checkoutUrl = await createCheckoutSession(client, plan.name, selectedBillingCycle, selectedCurrency, applySetupFee, setupFeeServices);
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            }
        } catch (error) {
            // Error is handled by store useEffect
        } finally {
            setIsProcessing(false);
        }
    };

    const formatPrice = (price: number, currency: string = "usd") => {
        if (price === undefined || price === null) {
            return "";
        }
        const normalizedCurrency = String(currency || "usd").toLowerCase();
        const symbol = CURRENCY_META[normalizedCurrency]?.symbol || normalizedCurrency.toUpperCase();
        return `${symbol}${price.toLocaleString()}`;
    };

    const getCurrencySymbol = (currency: string) => {
        const normalizedCurrency = String(currency || "usd").toLowerCase();
        return CURRENCY_META[normalizedCurrency]?.symbol || normalizedCurrency.toUpperCase();
    };

    const setupFeeStatus = setupFeeStatusData?.myOrganizationSetupFeeStatus;
    const hasPaidSetupFee = Boolean(setupFeeStatus?.alreadyPaid);
    const paidSetupFeeAmount = Number(setupFeeStatus?.amount || 0);
    const paidSetupFeeCurrency = String(setupFeeStatus?.currency || selectedCurrency || "usd").toLowerCase();
    const paidSetupFeeDateText = setupFeeStatus?.chargedAt ? new Date(setupFeeStatus.chargedAt).toLocaleDateString() : null;

    const getPlanSetupFeeEntry = (plan: any) =>
        plan?.setupFeePrices?.find((sfp: any) => String(sfp.currency || "").toLowerCase() === String(selectedCurrency || "").toLowerCase()) || null;
    const getPlanSetupFeeAmount = (plan: any) => {
        const entry = getPlanSetupFeeEntry(plan);
        return entry ? Number(entry.amount || 0) : Number(plan?.setupFee || 0);
    };
    const getPlanChatSetupFee = (plan: any) => Number(getPlanSetupFeeEntry(plan)?.chatAmount || 0);
    const getPlanCallSetupFee = (plan: any) => Number(getPlanSetupFeeEntry(plan)?.callAmount || 0);
    const planHasServiceSplit = (plan: any) => getPlanChatSetupFee(plan) > 0 || getPlanCallSetupFee(plan) > 0;
    const getPlanSetupFeeCurrency = (plan: any) =>
        String(getPlanSetupFeeEntry(plan)?.currency || plan?.setupFeeCurrency || plan?.currency || selectedCurrency || "usd").toLowerCase();
    const shouldShowSetupFeeCheckbox = (plan: any) => isAuthenticated && !hasPaidSetupFee && getPlanSetupFeeAmount(plan) > 0;
    const shouldApplySetupFeeForPlan = (plan: any) => {
        if (!shouldShowSetupFeeCheckbox(plan)) return false;
        if (planHasServiceSplit(plan)) {
            const selected = setupServiceSelections[plan.id];
            return Boolean(selected && selected.size > 0);
        }
        return Boolean(setupFeeSelections[plan.id]);
    };
    const getPlanSetupFeeServices = (plan: any): SetupFeeService[] => {
        if (!planHasServiceSplit(plan)) return [];
        const selected = setupServiceSelections[plan.id];
        return selected ? Array.from(selected) : [];
    };
    const toggleSetupService = (planId: string, service: SetupFeeService) => {
        setSetupServiceSelections((prev) => {
            const next = { ...prev };
            const existing = new Set(next[planId] || []);
            if (existing.has(service)) existing.delete(service); else existing.add(service);
            next[planId] = existing;
            return next;
        });
    };
    const computePlanSetupFeeCharge = (plan: any) => {
        if (!shouldApplySetupFeeForPlan(plan)) return 0;
        if (planHasServiceSplit(plan)) {
            const services = getPlanSetupFeeServices(plan);
            let sum = 0;
            if (services.includes("chat")) sum += getPlanChatSetupFee(plan);
            if (services.includes("call")) sum += getPlanCallSetupFee(plan);
            return sum;
        }
        return getPlanSetupFeeAmount(plan);
    };

    const getDiscountPercentage = (monthlyPrice: number, yearlyPrice: number): number | null => {
        const monthlyYearly = monthlyPrice * 12;
        if (monthlyYearly <= 0) {
            return null;
        }

        const discount = ((monthlyYearly - yearlyPrice) / monthlyYearly) * 100;
        const roundedDiscount = Math.round(discount);

        if (!Number.isFinite(roundedDiscount) || roundedDiscount < 0) {
            return null;
        }

        return roundedDiscount;
    };

    const getCurrentPrice = (priceInfo: any) => {
        return selectedBillingCycle === "yearly" ? priceInfo.yearlyPrice : priceInfo.monthlyPrice;
    };

    const getBillingText = (priceInfo: any) => {
        const price = getCurrentPrice(priceInfo);
        const symbol = getCurrencySymbol(priceInfo.currency);

        if (selectedBillingCycle === "yearly") {
            const monthlyEquivalent = price / 12;
            return `${symbol}${Math.round(monthlyEquivalent).toLocaleString()}/month`;
        }
        return `${symbol}${price.toLocaleString()}/month`;
    };

    const isCurrentPlan = (planName: string) => {
        return currentSubscription?.plan === planName;
    };

    const setupFeeHelpText =
        "If you would like help getting everything set up, our professional team is available to assist you with the entire process, from installation and configuration to ensuring that the system is properly integrated and running smoothly. We will guide you through each step and make sure everything is optimized for your needs so you can start using the service without any technical difficulties.";

    const getStatusBadge = (planName: string) => {
        if (isCurrentPlan(planName)) {
            return <span className="pill pill-success">Current Plan</span>;
        }
        return null;
    };

    const processedPlans = pricingPlans
        .map((plan) => {
            const priceInfo = plan.prices.find((p) => p.currency === selectedCurrency);
            if (!priceInfo) {
                return null;
            }
            return { ...plan, ...priceInfo };
        })
        .filter((p) => p !== null)
        .sort((a, b) => {
            const aIsFree = a.name?.toLowerCase() === "free";
            const bIsFree = b.name?.toLowerCase() === "free";

            if (aIsFree && !bIsFree) return -1;
            if (!aIsFree && bIsFree) return 1;

            const aPrice = Number(selectedBillingCycle === "yearly" ? a.yearlyPrice : a.monthlyPrice) || 0;
            const bPrice = Number(selectedBillingCycle === "yearly" ? b.yearlyPrice : b.monthlyPrice) || 0;

            if (aPrice !== bPrice) return aPrice - bPrice;
            return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        });

    // Unauthenticated Pricing Page (Hero Design)
    if (!isAuthenticated) {
        return (
            <div className="bg-slate-900 min-h-screen overflow-x-hidden">
                <LandingNavbar />

                {/* Hero Section */}
                <div className="relative pt-32 pb-20">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="text-center">
                            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
                                Simple, transparent pricing
                            </motion.h1>
                            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-6 text-lg leading-8 text-slate-300 max-w-3xl mx-auto">
                                Choose the perfect plan for your business. Scale as you grow.
                            </motion.p>
                        </div>

                        {/* Currency and Billing Toggle */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-12">
                            {availableCurrencies.length > 1 && (
                            <div className="flex items-center bg-slate-800 rounded-lg p-1 border border-slate-700">
                                {availableCurrencies.map((currencyCode) => (
                                    <button
                                        key={currencyCode}
                                        onClick={() => setSelectedCurrency(asBillingCurrency(currencyCode))}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedCurrency === currencyCode ? "bg-orange-600 text-white" : "text-slate-300 hover:text-white"}`}
                                    >
                                        {currencyCode === "inr" ? <CurrencyRupeeIcon className="w-4 h-4" /> : <GlobeAltIcon className="w-4 h-4" />}
                                        {CURRENCY_META[currencyCode]?.label || currencyCode.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                            )}

                            {/* Billing Cycle Toggle */}
                            <div className="flex items-center bg-slate-800 rounded-lg p-1 border border-slate-700">
                                <button
                                    onClick={() => setSelectedBillingCycle("monthly")}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedBillingCycle === "monthly" ? "bg-orange-600 text-white" : "text-slate-300 hover:text-white"}`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setSelectedBillingCycle("yearly")}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${selectedBillingCycle === "yearly" ? "bg-orange-600 text-white" : "text-slate-300 hover:text-white"}`}
                                >
                                    Yearly
                                </button>
                            </div>
                        </motion.div>

                        {/* Pricing Cards */}
                        {isLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap justify-center gap-8 mt-16">
                                {processedPlans.map((plan, index) => (
                                    <motion.div
                                        key={plan.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className={`relative flex flex-col bg-slate-800 rounded-2xl shadow-lg border transition-all duration-300 hover:shadow-xl hover:scale-[1.02] w-full max-w-sm md:w-72 ${
                                            plan.isPopular ? "border-orange-500 pt-6" : "border-slate-700"
                                        }`}
                                    >
                                        {plan.isPopular && (
                                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                                <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">Most Popular</span>
                                            </div>
                                        )}

                                        <div className="p-8 h-full flex flex-col">
                                            {/* Plan Header */}
                                            <div className="text-center mb-8">
                                                <h3 className="text-2xl font-bold text-white mb-2">{plan.displayName}</h3>
                                                <p className="text-slate-300 text-sm mb-4">{plan.description}</p>

                                                {/* Price */}
                                                <div className="mb-4">
                                                    <div className="flex items-baseline justify-center">
                                                        <span className="text-4xl font-bold text-white">{formatPrice(getCurrentPrice(plan), selectedCurrency)}</span>
                                                        <span className="text-slate-300 ml-1">/{selectedBillingCycle === "yearly" ? "year" : "month"}</span>
                                                    </div>
                                                    {selectedBillingCycle === "yearly" && <div className="text-sm text-slate-400 mt-1">{getBillingText(plan)} billed annually</div>}
                                                    {selectedBillingCycle === "yearly" &&
                                                        (() => {
                                                            const discount = getDiscountPercentage(plan.monthlyPrice, plan.yearlyPrice);
                                                            if (discount === null) return null;
                                                            return <div className="text-sm text-orange-400 font-medium mt-1">Save {discount}%</div>;
                                                        })()}
                                                    <a href="#usage-pricing" className="mt-2 inline-block text-xs text-slate-400 hover:text-orange-400">
                                                        + usage for calls and SMS
                                                    </a>
                                                </div>
                                            </div>

                                            {/* Features */}
                                            <div className="space-y-4 mb-8 flex-1">
                                                {plan.features.map((feature, featureIndex) => (
                                                    <div key={featureIndex} className="flex items-start">
                                                        {feature.included ? <CheckIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" /> : <XMarkIcon className="w-5 h-5 text-slate-500 mt-0.5 mr-3 flex-shrink-0" />}
                                                        <span className={`text-sm ${feature.included ? "text-slate-200" : "text-slate-500"}`}>
                                                            {feature.name}
                                                            {planFeatureLimitLabel(feature) != null && <span className="text-slate-400 ml-1">({planFeatureLimitLabel(feature)})</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* CTA Button */}
                                            <button
                                                onClick={() => handleSelectPlan(plan.name)}
                                                disabled={isProcessing}
                                                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors mt-auto ${
                                                    plan.isPopular ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600" : "bg-slate-700 text-white hover:bg-slate-600"
                                                }`}
                                            >
                                                {isProcessing ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        Processing...
                                                    </div>
                                                ) : (
                                                    "Get Started"
                                                )}
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}

                                {/* Custom Plan Card */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: processedPlans.length * 0.1 }}
                                    className="relative flex flex-col bg-slate-800 rounded-2xl shadow-lg border border-slate-700 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] w-full max-w-sm md:w-72"
                                >
                                    <div className="p-8 h-full flex flex-col">
                                        {/* Plan Header */}
                                        <div className="text-center mb-8">
                                            <h3 className="text-2xl font-bold text-white mb-2">Custom Plan</h3>
                                            <p className="text-slate-300 text-sm mb-4">Need a tailored solution? Contact us for custom pricing and features.</p>

                                            {/* Price */}
                                            <div className="mb-4">
                                                <div className="flex items-baseline justify-center">
                                                    <span className="text-4xl font-bold text-white">Custom</span>
                                                </div>
                                                <div className="text-sm text-slate-400 mt-1">Tailored to your needs</div>
                                            </div>
                                        </div>

                                        {/* Features */}
                                        <div className="space-y-4 mb-8 flex-1">
                                            <div className="flex items-start">
                                                <CheckIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                                                <span className="text-sm text-slate-200">Custom feature limits</span>
                                            </div>
                                            <div className="flex items-start">
                                                <CheckIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                                                <span className="text-sm text-slate-200">Dedicated support</span>
                                            </div>
                                            <div className="flex items-start">
                                                <CheckIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                                                <span className="text-sm text-slate-200">Flexible billing</span>
                                            </div>
                                            <div className="flex items-start">
                                                <CheckIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                                                <span className="text-sm text-slate-200">Priority assistance</span>
                                            </div>
                                        </div>

                                        {/* CTA Button */}
                                        <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-medium transition-colors text-center bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600 mt-auto">
                                            Contact Us
                                        </Link>
                                    </div>
                                </motion.div>
                            </div>
                        )}

                        <div className="mt-24">
                            <UsagePricing tone="dark" currency={selectedCurrency} plans={pricingPlans} structuredData />
                        </div>
                    </div>
                </div>

                <FAQSection
                    id="pricing"
                    eyebrow="Pricing · FAQ"
                    heading="Pricing FAQ"
                    intro="Answers to the most common questions about FloGPT plans, billing, and usage."
                    items={PRICING_FAQS}
                />

                <Footer />

                {/* Product/AggregateOffer feeds Google's price snippet and AI
                    pricing summaries. Each plan from the backend becomes an
                    Offer with its monthly or yearly price in the active
                    currency. */}
                <JsonLd id="pricing-product" data={productSchema} />
            </div>
        );
    }

    // Shopify-managed orgs see a focused Shopify Billing panel instead of the
    // Stripe-powered plan picker. Rule 1.2.1 disallows off-platform billing
    // for App Store apps, so we replace the entire plan grid with a deep link
    // back to the embedded /app/billing route which surfaces Shopify's
    // standard billing modal on confirm.
    // An authenticated visitor whose rail we can't confirm yet must not be shown
    // the Stripe grid — that ambiguity is how a Shopify merchant reached checkout.
    if (isAuthenticated && !stripeRailAllowed && !isShopifyManaged) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold text-brand-ink">Pricing Plans</h1>
                </div>
                <div className="flex justify-center py-16">
                    <Spinner size="lg" tone="brand" label="Loading your billing profile…" />
                </div>
            </div>
        );
    }

    if (isAuthenticated && isShopifyManaged) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold text-brand-ink">Pricing Plans</h1>
                    <p className="text-brand-ink/60 mt-2">All plan changes are billed through Shopify.</p>
                </div>
                <div className="rounded-panel border-2 border-brand-orange/40 bg-brand-cream/40 p-6">
                    <div className="flex items-start gap-4">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-card bg-brand-orange/20 border border-brand-orange/40">
                            <SparklesIcon className="h-5 w-5 text-brand-orange" />
                        </div>
                        <div className="flex-1 space-y-3">
                            <div>
                                <h2 className="text-lg font-bold text-brand-ink leading-tight">Plan changes are managed in Shopify</h2>
                                <p className="mt-1 text-sm text-brand-ink/70">
                                    Because FloGPT is installed via the Shopify App Store, every paid plan change — subscribe, upgrade, downgrade, cancel — is handled inside Shopify's standard billing modal. Your store's payment method on file is charged and the subscription appears on your regular Shopify invoice.
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    if (shopifyBillingUrl) {
                                        window.open(shopifyBillingUrl, "_blank", "noopener,noreferrer");
                                    } else {
                                        window.open("https://admin.shopify.com", "_blank", "noopener,noreferrer");
                                    }
                                }}
                                className="rounded-button bg-brand-orange px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-orange/90"
                            >
                                Open Shopify Billing
                            </button>
                            <p className="border-t border-brand-line/60 pt-3 text-xs text-brand-ink/50">
                                Opt-in add-ons — phone numbers, call minutes, SMS and managed-support seats — are billed as Shopify usage charges on the same invoice. No card is ever needed in this dashboard.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Authenticated Pricing Page (Dashboard Design)
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-brand-ink">Pricing Plans</h1>
                <p className="text-brand-ink/60 mt-2">Choose the perfect plan for your business needs</p>
            </div>

            <ManagedSupportPromo variant="banner" />

            {/* Currency and Billing Toggle */}
            <div className="bg-white rounded-panel shadow-card border border-brand-line p-6">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                    {availableCurrencies.length > 1 && (
                    <div className="flex items-center bg-brand-cream/40 rounded-card p-1 border border-brand-line">
                        {availableCurrencies.map((currencyCode) => (
                            <button
                                key={currencyCode}
                                onClick={() => setSelectedCurrency(asBillingCurrency(currencyCode))}
                                className={`flex items-center gap-2 px-4 py-2 rounded-button text-sm font-semibold transition-colors ${selectedCurrency === currencyCode ? "bg-brand-orange text-white" : "text-brand-ink hover:text-brand-ink/80"}`}
                            >
                                {currencyCode === "inr" ? <CurrencyRupeeIcon className="w-4 h-4" /> : <GlobeAltIcon className="w-4 h-4" />}
                                {CURRENCY_META[currencyCode]?.label || currencyCode.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    )}

                    {/* Billing Cycle Toggle */}
                    <div className="flex items-center bg-brand-cream/40 rounded-card p-1 border border-brand-line">
                        <button
                            onClick={() => setSelectedBillingCycle("monthly")}
                            className={`px-4 py-2 rounded-button text-sm font-semibold transition-colors ${selectedBillingCycle === "monthly" ? "bg-brand-orange text-white" : "text-brand-ink hover:text-brand-ink/80"}`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setSelectedBillingCycle("yearly")}
                            className={`px-4 py-2 rounded-button text-sm font-semibold transition-colors ${selectedBillingCycle === "yearly" ? "bg-brand-orange text-white" : "text-brand-ink hover:text-brand-ink/80"}`}
                        >
                            Yearly
                        </button>
                    </div>
                </div>
            </div>

            {hasPaidSetupFee && paidSetupFeeAmount > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-card px-4 py-3 text-sm">
                    One-time setup fee already paid: {getCurrencySymbol(paidSetupFeeCurrency)}
                    {paidSetupFeeAmount.toFixed(2)}
                    {paidSetupFeeDateText ? ` on ${paidSetupFeeDateText}` : ""}.
                </div>
            )}

            {/* Pricing Cards */}
            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <Spinner size="lg" />
                </div>
            ) : (
                <div className="flex flex-wrap justify-center gap-6">
                    {processedPlans.map((plan) => (
                        <div
                            key={plan.id}
                            className={`relative flex flex-col bg-white rounded-panel shadow-card border-2 transition-all duration-300 hover:shadow-panel hover:scale-[1.02] w-full max-w-sm md:w-72 ${
                                plan.isPopular ? "border-brand-orange ring-2 ring-brand-orange/20 pt-4" : "border-brand-line"
                            }`}
                        >
                            {plan.isPopular && (
                                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-brand-orange text-white px-3 py-1 rounded-full text-xs font-bold shadow-card">Most Popular</span>
                                </div>
                            )}

                            <div className="p-6 h-full flex flex-col">
                                {/* Plan Header */}
                                <div className="text-center mb-6">
                                    <h3 className="text-xl font-bold text-brand-ink mb-2">{plan.displayName}</h3>
                                    <p className="text-brand-ink/60 text-sm mb-4">{plan.description}</p>

                                    {/* Price */}
                                    <div className="mb-4">
                                        <div className="flex items-baseline justify-center">
                                            <span className="text-3xl font-extrabold text-brand-ink">{formatPrice(getCurrentPrice(plan), selectedCurrency)}</span>
                                            <span className="text-brand-ink/60 ml-1 font-semibold">/{selectedBillingCycle === "yearly" ? "year" : "month"}</span>
                                        </div>
                                        {selectedBillingCycle === "yearly" &&
                                            (() => {
                                                const discount = getDiscountPercentage(plan.monthlyPrice, plan.yearlyPrice);
                                                if (discount === null) return null;
                                                return <div className="text-sm text-emerald-600 font-semibold mt-1">Save {discount}%</div>;
                                            })()}
                                        <a href="#usage-pricing" className="mt-2 inline-block text-xs text-brand-ink/50 hover:text-brand-orange">
                                            + usage for calls and SMS
                                        </a>
                                    </div>

                                    {/* Status Badge */}
                                    {getStatusBadge(plan.name)}
                                </div>

                                {/* Features */}
                                <div className="space-y-3 mb-6 flex-1">
                                    {plan.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-start">
                                            {feature.included ? <CheckIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" /> : <XMarkIcon className="w-4 h-4 text-brand-ink/30 mt-0.5 mr-3 flex-shrink-0" />}
                                            <span className={`text-sm ${feature.included ? "text-brand-ink" : "text-brand-ink/40"}`}>
                                                {feature.name}
                                                {planFeatureLimitLabel(feature) != null && <span className="text-brand-ink/60 ml-1">({planFeatureLimitLabel(feature)})</span>}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {shouldShowSetupFeeCheckbox(plan) && planHasServiceSplit(plan) && (
                                    <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-3 py-2">
                                        <div className="text-sm font-semibold text-amber-900 mb-1">Add one-time setup fee</div>
                                        <div className="space-y-1">
                                            {getPlanChatSetupFee(plan) > 0 && (
                                                <label className="flex items-center justify-between gap-2 text-sm text-amber-900 cursor-pointer">
                                                    <span className="inline-flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                                            checked={Boolean(setupServiceSelections[plan.id]?.has("chat"))}
                                                            onChange={() => toggleSetupService(plan.id, "chat")}
                                                        />
                                                        <span>Chat setup</span>
                                                    </span>
                                                    <span className="tabular-nums">
                                                        {getCurrencySymbol(getPlanSetupFeeCurrency(plan))}
                                                        {getPlanChatSetupFee(plan).toFixed(2)}
                                                    </span>
                                                </label>
                                            )}
                                            {getPlanCallSetupFee(plan) > 0 && (
                                                <label className="flex items-center justify-between gap-2 text-sm text-amber-900 cursor-pointer">
                                                    <span className="inline-flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                                            checked={Boolean(setupServiceSelections[plan.id]?.has("call"))}
                                                            onChange={() => toggleSetupService(plan.id, "call")}
                                                        />
                                                        <span>Call setup</span>
                                                    </span>
                                                    <span className="tabular-nums">
                                                        {getCurrencySymbol(getPlanSetupFeeCurrency(plan))}
                                                        {getPlanCallSetupFee(plan).toFixed(2)}
                                                    </span>
                                                </label>
                                            )}
                                        </div>
                                        {computePlanSetupFeeCharge(plan) > 0 && (
                                            <div className="mt-2 flex items-center justify-between border-t border-amber-200 pt-2 text-xs font-semibold text-amber-900">
                                                <span>Setup subtotal</span>
                                                <span className="tabular-nums">
                                                    {getCurrencySymbol(getPlanSetupFeeCurrency(plan))}
                                                    {computePlanSetupFeeCharge(plan).toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        <p className="mt-2 text-xs text-amber-800/90">{setupFeeDescriptionExpanded[plan.id] ? setupFeeHelpText : `${setupFeeHelpText.slice(0, 160)}...`}</p>
                                        <button
                                            type="button"
                                            className="mt-1 text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                                            onClick={() =>
                                                setSetupFeeDescriptionExpanded((prev) => ({
                                                    ...prev,
                                                    [plan.id]: !prev[plan.id],
                                                }))
                                            }
                                        >
                                            {setupFeeDescriptionExpanded[plan.id] ? "See less" : "See more"}
                                        </button>
                                    </div>
                                )}
                                {shouldShowSetupFeeCheckbox(plan) && !planHasServiceSplit(plan) && (
                                    <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-3 py-2">
                                        <label className="flex items-start gap-2 text-sm text-amber-900">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                                                checked={Boolean(setupFeeSelections[plan.id])}
                                                onChange={(e) =>
                                                    setSetupFeeSelections((prev) => ({
                                                        ...prev,
                                                        [plan.id]: e.target.checked,
                                                    }))
                                                }
                                            />
                                            <span>
                                                Add one-time setup fee: {getCurrencySymbol(getPlanSetupFeeCurrency(plan))}
                                                {getPlanSetupFeeAmount(plan).toFixed(2)}
                                            </span>
                                        </label>
                                        <p className="mt-1 text-xs text-amber-800/90">{setupFeeDescriptionExpanded[plan.id] ? setupFeeHelpText : `${setupFeeHelpText.slice(0, 160)}...`}</p>
                                        <button
                                            type="button"
                                            className="mt-1 text-xs font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                                            onClick={() =>
                                                setSetupFeeDescriptionExpanded((prev) => ({
                                                    ...prev,
                                                    [plan.id]: !prev[plan.id],
                                                }))
                                            }
                                        >
                                            {setupFeeDescriptionExpanded[plan.id] ? "See less" : "See more"}
                                        </button>
                                    </div>
                                )}
                                {!shouldShowSetupFeeCheckbox(plan) && hasPaidSetupFee && paidSetupFeeAmount > 0 && (
                                    <div className="mb-4 text-xs text-emerald-700">
                                        Setup fee paid: {getCurrencySymbol(paidSetupFeeCurrency)}
                                        {paidSetupFeeAmount.toFixed(2)}
                                    </div>
                                )}

                                {/* CTA Button */}
                                <button
                                    onClick={() => handleSelectPlan(plan.name)}
                                    disabled={isProcessing || isCurrentPlan(plan.name)}
                                    className={`w-full py-2.5 px-4 rounded-button font-semibold transition-colors mt-auto ${
                                        isCurrentPlan(plan.name)
                                            ? "bg-brand-cream text-brand-ink/40 cursor-not-allowed"
                                            : plan.isPopular
                                                ? "bg-brand-orange text-white hover:bg-brand-orange/90"
                                                : "bg-brand-ink text-white hover:bg-brand-ink/90"
                                    }`}
                                >
                                    {isProcessing ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <Spinner size="sm" tone="onDark" />
                                            Processing...
                                        </div>
                                    ) : (
                                        "Get Started"
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Custom Plan Card */}
                    <div className="relative flex flex-col bg-white rounded-panel shadow-card border-2 border-brand-line transition-all duration-300 hover:shadow-panel hover:scale-[1.02] w-full max-w-sm md:w-72">
                        <div className="p-6 h-full flex flex-col">
                            {/* Plan Header */}
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold text-brand-ink mb-2">Custom Plan</h3>
                                <p className="text-brand-ink/60 text-sm mb-4">Need a tailored solution? Contact us for custom pricing and features.</p>

                                {/* Price */}
                                <div className="mb-4">
                                    <div className="flex items-baseline justify-center">
                                        <span className="text-3xl font-extrabold text-brand-ink">Custom</span>
                                    </div>
                                    <div className="text-sm text-brand-ink/60 mt-1">Tailored to your needs</div>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="space-y-3 mb-6 flex-1">
                                <div className="flex items-start">
                                    <CheckIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                                    <span className="text-sm text-brand-ink">Custom feature limits</span>
                                </div>
                                <div className="flex items-start">
                                    <CheckIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                                    <span className="text-sm text-brand-ink">Dedicated support</span>
                                </div>
                                <div className="flex items-start">
                                    <CheckIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                                    <span className="text-sm text-brand-ink">Flexible billing</span>
                                </div>
                                <div className="flex items-start">
                                    <CheckIcon className="w-4 h-4 text-emerald-500 mt-0.5 mr-3 flex-shrink-0" />
                                    <span className="text-sm text-brand-ink">Priority assistance</span>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <Link to="/contact" className="block w-full py-2.5 px-4 rounded-button font-semibold transition-colors text-center bg-brand-ink text-white hover:bg-brand-ink/90 mt-auto">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            <UsagePricing tone="light" currency={selectedCurrency} plans={pricingPlans} />
        </div>
    );
};

export default Pricing;
